import { FormattedMessage } from 'react-intl';

import { getChannelDefaults } from '@/components/pages/job-posting/utils/channel-defaults';
import { Button } from '@/components/ui/button';
import { Typography } from '@/components/ui/typography';
import { useJobPostingContext } from '@/contexts/job-posting-context';

import { OpeningItem } from './opening-item';

interface OpeningsListProps {
  channelIcon: React.ReactNode;
  channelKey: string;
  displayTitle: string;
  recommendedBudget?: number;
  draftSelectedOpenings: Record<string, number | null>;
  onDraftChange: (selectedOpenings: Record<string, number | null>) => void;
  openingsSelectedForAnotherProduct: Set<string>;
}
export function OpeningsList({
  channelIcon,
  channelKey,
  displayTitle,
  recommendedBudget,
  draftSelectedOpenings,
  onDraftChange,
  openingsSelectedForAnotherProduct,
}: OpeningsListProps) {
  const { selectedOpeningIds, hookForm } = useJobPostingContext();
  const { watch } = hookForm;

  const channelSlug = watch(`channels.${channelKey}.slug`);
  const selectedOpeningsForChannel = Object.keys(draftSelectedOpenings);
  const unsupportedOpeningsForChannel =
    watch(`channels.${channelKey}.unsupportedOpenings`) ?? [];

  /* Check if all supported openings are selected for the channel,used to show the "Select all" or "Unselect all" button */
  const isAllSupportedOpeningsSelected =
    selectedOpeningIds
      .filter(
        openingId =>
          !unsupportedOpeningsForChannel.includes(openingId) &&
          !openingsSelectedForAnotherProduct.has(openingId),
      )
      .every(openingId => openingId in draftSelectedOpenings) ?? false;

  /* Handle the click on the "Select all" button */
  const handleSelectAllClick = () => {
    const next = { ...draftSelectedOpenings };
    for (const openingId of selectedOpeningIds) {
      if (unsupportedOpeningsForChannel.includes(openingId)) continue;
      if (openingsSelectedForAnotherProduct.has(openingId)) continue;
      if (openingId in next) continue;
      next[openingId] =
        recommendedBudget ?? getChannelDefaults(channelSlug).budget ?? null;
    }
    onDraftChange(next);
  };

  /* Handle the click on the "Unselect all" button */
  const handleUnselectAllClick = () => {
    onDraftChange({});
  };

  /* Handle the change of the selection of an opening */
  const handleOpeningSelectionChange = (openingId: string) => {
    const next = { ...draftSelectedOpenings };

    if (selectedOpeningsForChannel.includes(openingId)) {
      delete next[openingId];
    } else {
      next[openingId] =
        recommendedBudget ?? getChannelDefaults(channelSlug).budget ?? null;
    }

    onDraftChange(next);
  };

  return (
    <div className="flex flex-col gap-6 overflow-hidden p-4 pb-0">
      <div className="flex items-center justify-between gap-2">
        <div className="flex flex-col items-start gap-1">
          {channelIcon}
          <Typography variant="body2" className="font-bold">
            {displayTitle}
          </Typography>
        </div>

        {isAllSupportedOpeningsSelected ? (
          <Button
            variant="secondary"
            type="button"
            onClick={handleUnselectAllClick}
          >
            <FormattedMessage
              defaultMessage="Unselect all"
              description="Button to unselect all openings for a job board"
            />
          </Button>
        ) : (
          <Button
            variant="secondary"
            type="button"
            onClick={handleSelectAllClick}
          >
            <FormattedMessage
              defaultMessage="Select all"
              description="Button to select all openings for a job board"
            />
          </Button>
        )}
      </div>

      <Typography variant="small1" className="-mb-6">
        <FormattedMessage
          defaultMessage="Select which to include"
          description="Title of the modal for selecting openings for a job board"
        />
      </Typography>
      <div className="divide-fx-neutral-200 flex flex-col divide-y overflow-y-auto">
        {selectedOpeningIds.map(openingId => (
          <OpeningItem
            key={openingId}
            openingId={openingId}
            isSelected={selectedOpeningsForChannel.includes(openingId)}
            isUnsupported={unsupportedOpeningsForChannel.includes(openingId)}
            isAlreadySelected={openingsSelectedForAnotherProduct.has(openingId)}
            onSelectionChange={handleOpeningSelectionChange}
          />
        ))}
      </div>
    </div>
  );
}
